import React, { useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { customerDisplayName } from '../lib/customerDirectory'
import BikeFields, { EMPTY_BIKE, hasBikeDetails } from './BikeFields'

export default function AddBikeModal(props) {
  return <ScopedAddBikeModal key={`${props.workshopId}:${props.customer.id}`} {...props} />
}

function ScopedAddBikeModal({ workshopId, customer, bikes, onSaved, onClose }) {
  const [form, setForm] = useState(EMPTY_BIKE)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const attempt = useRef(null)
  const active = useRef(false)
  const busy = useRef(false)
  const controller = useRef(null)
  const [recovering, setRecovering] = useState(false)
  const [confirmedBike, setConfirmedBike] = useState(null)
  useEffect(() => {
    active.current = true
    return () => { active.current = false; controller.current?.abort() }
  }, [])
  const similar = bikes.some(bike => bike.customer_id === customer.id && form.brand.trim() && form.model.trim()
    && String(bike.brand || '').toLowerCase() === form.brand.trim().toLowerCase()
    && String(bike.model || '').toLowerCase() === form.model.trim().toLowerCase())
  async function save(event) {
    event.preventDefault()
    if (busy.current || confirmedBike) return
    if (!hasBikeDetails(attempt.current?.bike || form)) { setError('Enter some bike details.'); return }
    setSaving(true)
    setError('')
    busy.current = true
    const isRecovery = recovering
    let dispatched = false
    let rejectedBeforeInsert = false
    const requestController = new AbortController()
    controller.current = requestController
    const isCurrent = () => active.current && !requestController.signal.aborted
    try {
      if (!attempt.current) {
        const details = Object.fromEntries(Object.keys(EMPTY_BIKE).map(key => [key,
          key === 'is_ebike' ? form[key] === true : String(form[key] || '').trim(),
        ]))
        attempt.current = { workshopId, customerId: customer.id, bikeId: crypto.randomUUID(), bike: details }
      }
      const { data, error: sessionError } = await supabase.auth.getSession()
      if (!isCurrent()) return
      if (sessionError || !data?.session?.access_token) throw new Error('Please sign in again before saving.')
      dispatched = true
      const response = await fetch('/.netlify/functions/create-customer-bike', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${data.session.access_token}` },
        body: JSON.stringify(attempt.current), signal: requestController.signal,
      })
      const payload = await response.json()
      if (!isCurrent()) return
      if (!response.ok) {
        rejectedBeforeInsert = response.status >= 400 && response.status < 500 && response.status !== 409
        throw new Error(payload.error || 'Could not save bike.')
      }
      if (payload.bike?.id !== attempt.current.bikeId || payload.bike.workshop_id !== workshopId || payload.bike.customer_id !== customer.id
        || Object.entries(attempt.current.bike).some(([key, value]) => (key === 'is_ebike' ? payload.bike[key] === true : String(payload.bike[key] || '').trim()) !== value)) {
        throw new Error('The original bike details could not be confirmed. Retry the original save.')
      }
      if (isRecovery) setConfirmedBike(payload.bike)
      else onSaved(payload.bike)
    } catch (err) {
      if (isCurrent()) {
        setError(err.message || 'Could not save bike.')
        if (!isRecovery && (!dispatched || rejectedBeforeInsert)) attempt.current = null
        else setRecovering(true)
      }
    } finally {
      if (isCurrent()) { busy.current = false; setSaving(false) }
    }
  }
  return <div className="modal-overlay"><form className="modal" role="dialog" aria-modal="true" aria-labelledby="add-bike-title" onSubmit={save}>
    <div className="modal-header"><div className="modal-title" id="add-bike-title">Add Bike</div><button type="button" className="close-btn" aria-label="Close Add Bike" disabled={saving} onClick={onClose}>×</button></div>
    <p>New bike for <strong>{customerDisplayName(customer)}</strong></p>
    {error && <div className="alert alert-error" role="alert">{error}</div>}
    {recovering && <div className="alert alert-info" role="status">
      {confirmedBike ? 'Original save confirmed. Use this saved bike to return to the customer.' : 'The original save is not confirmed. Its details are locked so a retry cannot create a second bike or silently change the first.'}
      <div>Confirm the original save before making a separate change or adding another physical bike.</div>
    </div>}
    <BikeFields form={recovering ? attempt.current.bike : form} disabled={saving || recovering} onChange={(key, value) => setForm(current => ({ ...current, [key]: value }))} />
    {similar && <p className="alert alert-info">This customer already has a bike with this brand and model. Save only if this is another physical bike; it will get its own record.</p>}
    <div className="action-row">
      <button type="button" className="btn btn-ghost" disabled={saving} onClick={onClose}>{recovering && !confirmedBike ? 'Close without confirming' : 'Cancel'}</button>
      {confirmedBike
        ? <button type="button" className="btn btn-primary" onClick={() => { if (active.current) onSaved(confirmedBike) }}>Use saved bike</button>
        : <button className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : recovering ? 'Retry original save' : 'Save new bike'}</button>}
    </div>
  </form></div>
}
